import { useCallback, useEffect, useState } from "react";

/**
 * Los datos reales de Google Ads de las keywords de un cliente (24/10/2026).
 *
 * Jorge: «que en las estrategias y donde están las keywords estén los datos
 * reales de Google Ads de la zona: por ejemplo "diseño web bilbao", cuánta gente
 * lo busca en Bizkaia; y así con todas».
 *
 * Los pide BilboBot a Leadslink, que los guarda un mes. La zona se guarda por
 * cliente: la primera vez es Bizkaia.
 */

export interface MetricaDeGoogle {
  busquedas: number;
  competencia: string | null;
  indice: number;
  cpcBajo: number | null;
  cpcAlto: number | null;
  meses: { mes: string; busquedas: number }[];
}

export interface DatosDeGoogle {
  zona: string;
  cargando: boolean;
  error: string | null;
  /** Por keyword en minúsculas. */
  porKeyword: Record<string, MetricaDeGoogle>;
  cambiarZona: (zona: string) => void;
}

export const ZONAS = ["Bizkaia", "Gipuzkoa", "Araba", "Cantabria", "Navarra", "La Rioja", "Madrid", "Barcelona", "España"];

export function useGoogleAds(clienteId: string, cuantas: number): DatosDeGoogle {
  const [zona, setZona] = useState("");
  const [porKeyword, setPorKeyword] = useState<Record<string, MetricaDeGoogle>>({});
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const cargar = useCallback(
    async (z?: string) => {
      setCargando(true);
      setError(null);
      try {
        const r = await fetch(
          `/api/memoria/clientes/${clienteId}/google-ads${z ? `?zona=${encodeURIComponent(z)}` : ""}`,
          { credentials: "same-origin" },
        );
        const d = (await r.json()) as { zona?: string; keywords?: Record<string, MetricaDeGoogle>; error?: string };
        if (!r.ok) throw new Error(d.error ?? `El servidor contestó ${r.status}`);
        setZona(d.zona ?? z ?? "Bizkaia");
        setPorKeyword(
          Object.fromEntries(Object.entries(d.keywords ?? {}).map(([k, v]) => [k.trim().toLowerCase(), v])),
        );
      } catch (e) {
        setError((e as Error).message);
      } finally {
        setCargando(false);
      }
    },
    [clienteId],
  );

  /* Al cambiar de cliente, o de número de keywords (una añadida), se vuelve a mirar. */
  useEffect(() => {
    setPorKeyword({});
    void cargar();
  }, [cargar, cuantas]);

  const cambiarZona = (z: string) => {
    const limpia = z.trim();
    if (!limpia) return;
    setZona(limpia);
    void fetch(`/api/memoria/clientes/${clienteId}/google-ads/zona`, {
      method: "POST",
      credentials: "same-origin",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ zona: limpia }),
    }).catch(() => undefined);
    void cargar(limpia);
  };

  return { zona, cargando, error, porKeyword, cambiarZona };
}

const COMPETENCIA: Record<string, [string, string]> = {
  LOW: ["baja", "es-ok"],
  MEDIUM: ["media", "es-aviso"],
  HIGH: ["alta", "es-mal"],
};

export function Competencia({ m }: { m: MetricaDeGoogle | undefined }) {
  if (!m?.competencia || !COMPETENCIA[m.competencia]) return <span className="sin">—</span>;
  const [t, c] = COMPETENCIA[m.competencia]!;
  return (
    <span className={`kg-competencia ${c}`} title={`Índice de competencia ${m.indice}/100`}>
      {t}
    </span>
  );
}

const euros = (n: number) => n.toLocaleString("es-ES", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export function Cpc({ m }: { m: MetricaDeGoogle | undefined }) {
  if (!m || (!m.cpcBajo && !m.cpcAlto)) return <span className="sin">—</span>;
  if (m.cpcBajo && m.cpcAlto) return <span title="Puja por salir arriba en Google: de la baja a la alta">{euros(m.cpcBajo)}–{euros(m.cpcAlto)} €</span>;
  return <span>{euros((m.cpcBajo ?? m.cpcAlto)!)} €</span>;
}

/** Si sube o baja: los tres últimos meses contra los tres anteriores. */
export function tendencia(m: MetricaDeGoogle | undefined): number | null {
  const v = (m?.meses ?? []).map((x) => x.busquedas);
  if (v.length < 6) return null;
  const ult = v.slice(-3).reduce((a, b) => a + b, 0);
  const ant = v.slice(-6, -3).reduce((a, b) => a + b, 0);
  if (ant === 0) return null;
  return Math.round(((ult - ant) / ant) * 100);
}

export function Busquedas({ m, cargando }: { m: MetricaDeGoogle | undefined; cargando: boolean }) {
  if (!m) return <span className="sin">{cargando ? "…" : "—"}</span>;
  const t = tendencia(m);
  return (
    <>
      <strong>{m.busquedas.toLocaleString("es-ES")}</strong>
      {t !== null && Math.abs(t) >= 10 && (
        <span className={t > 0 ? "mejor" : "peor"} title="Últimos 3 meses contra los 3 anteriores">
          {t > 0 ? "▲" : "▼"}
          {Math.abs(t)}%
        </span>
      )}
    </>
  );
}

/** Las barras de los 12 meses, pequeñas. */
function Barras({ m }: { m: MetricaDeGoogle }) {
  const max = Math.max(1, ...m.meses.map((x) => x.busquedas));
  return (
    <span className="kg-barras" aria-hidden>
      {m.meses.map((x) => (
        <span key={x.mes} title={`${x.mes}: ${x.busquedas}`} style={{ height: `${Math.max(6, (x.busquedas / max) * 100)}%` }} />
      ))}
    </span>
  );
}

/** El selector de zona: las de siempre o cualquier otra escrita. */
export function SelectorDeZona({ datos }: { datos: DatosDeGoogle }) {
  const [otra, setOtra] = useState("");
  const conocida = ZONAS.includes(datos.zona);
  return (
    <span className="kg-zona">
      Datos de Google Ads en{" "}
      <select
        value={conocida ? datos.zona : "__otra"}
        onChange={(e) => (e.target.value === "__otra" ? setOtra(datos.zona) : datos.cambiarZona(e.target.value))}
        aria-label="Zona de los datos de Google Ads"
      >
        {ZONAS.map((z) => (
          <option key={z} value={z}>
            {z}
          </option>
        ))}
        <option value="__otra">{conocida ? "Otra zona…" : datos.zona}</option>
      </select>
      {(!conocida || otra) && (
        <form
          onSubmit={(e) => {
            e.preventDefault();
            datos.cambiarZona(otra);
            setOtra("");
          }}
        >
          <input value={otra} placeholder="Provincia o ciudad" onChange={(e) => setOtra(e.target.value)} />
        </form>
      )}
      {datos.cargando && <span className="pista"> mirando…</span>}
      {datos.error && <span className="peor"> {datos.error}</span>}
    </span>
  );
}

/** Para la Estrategia: todas las keywords del proyecto, de la más buscada a la menos. */
export function LoQueSeBusca({
  keywords,
  datos,
}: {
  keywords: { texto: string; tipo: string; principal: boolean }[];
  datos: DatosDeGoogle;
}) {
  const filas = keywords
    .map((k) => ({ k, m: datos.porKeyword[k.texto.trim().toLowerCase()] }))
    .sort((a, b) => (b.m?.busquedas ?? -1) - (a.m?.busquedas ?? -1));
  const total = filas.reduce((n, f) => n + (f.m?.busquedas ?? 0), 0);
  return (
    <section className="caja-seo kg-loquesebusca">
      <header>
        <h3>Lo que se busca en {datos.zona || "la zona"}</h3>
        <SelectorDeZona datos={datos} />
      </header>
      {keywords.length === 0 ? (
        <p className="pista">Este cliente no tiene keywords en su estrategia. Se añaden en «El cliente» → Keywords.</p>
      ) : (
        <>
          <p className="pista">
            Las {keywords.length} keywords del proyecto suman <strong>{total.toLocaleString("es-ES")}</strong> búsquedas al
            mes en {datos.zona}, según Google Ads (media de los últimos 12 meses).
          </p>
          <div className="kg-tabla">
            <div className="kg-fila kg-cabecera">
              <span>Búsqueda</span>
              <span>Al mes</span>
              <span>12 meses</span>
              <span>Competencia</span>
              <span>Coste por clic</span>
            </div>
            {filas.map(({ k, m }) => (
              <div className={`kg-fila${k.principal ? " principal" : ""}`} key={k.texto}>
                <span className="kg-texto">
                  {k.principal && "★ "}
                  {k.texto}
                  <span className="pista"> · {k.tipo === "informativa" ? "informativa" : "de servicio"}</span>
                </span>
                <span className="kg-num">
                  <Busquedas m={m} cargando={datos.cargando} />
                </span>
                <span>{m && m.meses.length > 0 ? <Barras m={m} /> : <span className="sin">—</span>}</span>
                <span>
                  <Competencia m={m} />
                </span>
                <span className="kg-num">
                  <Cpc m={m} />
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
